import { combineReducers } from 'redux'
import { createSlice } from '@reduxjs/toolkit'
import {
  accountsDisburse,
  addPermission,
  deleteAssistant,
  deleteCondition,
  deleteOfficer,
  deleteTag,
  destroyAccountsDisburse,
  destroyDisburseValidate,
  destroyLoans,
  disburseValidate,
  loanSearch,
  loanSearchAgain,
  stopAccruals,
  undoStopAccruals,
} from './loan.actions'
import { LoanSearchParams } from '@lk/api'

type InitialState<T> = {
  status: 'idle' | 'loading' | 'loaded' | 'failed'
  error: string | null
  data: T
}

const stopAccuralsInitialState: InitialState<null> = {
  status: 'idle',
  error: null,
  data: null,
}

const stopAccuralsSlice = createSlice({
  name: 'stopAccurals',
  initialState: stopAccuralsInitialState,
  reducers: {},
  extraReducers: {
    [stopAccruals.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [stopAccruals.fulfilled.toString()]: (state) => {
      state.status = 'loaded'
    },
    [stopAccruals.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

const undoStopAccrualsInitialState: InitialState<null> = {
  status: 'idle',
  error: null,
  data: null,
}

const undoStopAccuralsSlice = createSlice({
  name: 'undoStopAccruals',
  initialState: undoStopAccrualsInitialState,
  reducers: {},
  extraReducers: {
    [undoStopAccruals.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [undoStopAccruals.fulfilled.toString()]: (state) => {
      state.status = 'loaded'
    },
    [undoStopAccruals.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

const deleteConditionInitialState: InitialState<null> = {
  status: 'idle',
  error: null,
  data: null,
}

const deleteConditionSlice = createSlice({
  name: 'deleteCondition',
  initialState: deleteConditionInitialState,
  reducers: {},
  extraReducers: {
    [deleteCondition.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [deleteCondition.fulfilled.toString()]: (state) => {
      state.status = 'loaded'
    },
    [deleteCondition.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

const deleteOfficerInitialState: InitialState<null> = {
  status: 'idle',
  error: null,
  data: null,
}

const deleteOfficerSlice = createSlice({
  name: 'deleteOfficer',
  initialState: deleteOfficerInitialState,
  reducers: {},
  extraReducers: {
    [deleteOfficer.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [deleteOfficer.fulfilled.toString()]: (state) => {
      state.status = 'loaded'
    },
    [deleteOfficer.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

const deleteAssistantInitialState: InitialState<null> = {
  status: 'idle',
  error: null,
  data: null,
}

const deleteAssistantSlice = createSlice({
  name: 'deleteAssistant',
  initialState: deleteAssistantInitialState,
  reducers: {},
  extraReducers: {
    [deleteAssistant.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [deleteAssistant.fulfilled.toString()]: (state) => {
      state.status = 'loaded'
    },
    [deleteAssistant.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

const addPermissionInitialState: InitialState<null> = {
  status: 'idle',
  error: null,
  data: null,
}

const addPermissionSlice = createSlice({
  name: 'addPermission',
  initialState: addPermissionInitialState,
  reducers: {},
  extraReducers: {
    [addPermission.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [addPermission.fulfilled.toString()]: (state) => {
      state.status = 'loaded'
    },
    [addPermission.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

const loanTagsInitialState: InitialState<null> = {
  status: 'idle',
  error: null,
  data: null,
}

const loanTagsSlice = createSlice({
  name: 'loanTags',
  initialState: loanTagsInitialState,
  reducers: {},
  extraReducers: {
    [deleteTag.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [deleteTag.fulfilled.toString()]: (state) => {
      state.status = 'loaded'
    },
    [deleteTag.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

const accountsDisburseInitialState: InitialState<null> = {
  status: 'loading',
  error: null,
  data: null,
}

const accountsDisburseSlice = createSlice({
  name: 'accountsDisburse',
  initialState: accountsDisburseInitialState,
  reducers: {},
  extraReducers: {
    [accountsDisburse.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [accountsDisburse.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      state.data = action.payload
      state.error = null
    },
    [accountsDisburse.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
      state.data = null
    },
    [destroyAccountsDisburse.toString()]: (state) => {
      return {
        ...state,
        ...accountsDisburseInitialState,
      }
    },
  },
})

const disburseValidateInitialState: InitialState<unknown> = {
  status: 'loading',
  error: null,
  data: null,
}

const disburseValidateSlice = createSlice({
  name: 'disburseValidate',
  initialState: disburseValidateInitialState,
  reducers: {},
  extraReducers: {
    [disburseValidate.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [disburseValidate.fulfilled.toString()]: (state, action) => {
      if (action.payload.saveData) {
        state.data = action.payload
      }
      state.status = 'loaded'
    },
    [disburseValidate.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
    [destroyDisburseValidate.toString()]: (state) => {
      return {
        ...state,
        ...disburseValidateInitialState,
      }
    },
  },
})

const loanDataInitialSlice: InitialState<unknown> & {
  prevSearchParams: LoanSearchParams | null
} = {
  status: 'idle',
  error: null,
  data: null,
  prevSearchParams: null,
}

const loandDataSlice = createSlice({
  name: 'loanData',
  initialState: loanDataInitialSlice,
  reducers: {},
  extraReducers: {
    [loanSearch.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [loanSearch.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      if (action.payload.instanceName) {
        state.data = action.payload.data
        state.prevSearchParams = action.meta.arg
      }
    },
    [loanSearch.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
      state.data = null
      state.prevSearchParams = null
    },
    [loanSearchAgain.pending.toString()]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [loanSearchAgain.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      state.data = action.payload
    },
    [loanSearchAgain.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
      state.data = null
      state.prevSearchParams = null
    },
    [destroyLoans.toString()]: () => {
      return {
        ...loanDataInitialSlice,
      }
    },
  },
})

export const loanReducer = combineReducers({
  stopAccruals: stopAccuralsSlice.reducer,
  undoStopAccruals: undoStopAccuralsSlice.reducer,
  deleteCondition: deleteConditionSlice.reducer,
  deleteOfficer: deleteOfficerSlice.reducer,
  deleteAssistant: deleteAssistantSlice.reducer,
  addPermission: addPermissionSlice.reducer,
  loanTags: loanTagsSlice.reducer,
  accountsDisburse: accountsDisburseSlice.reducer,
  disburseValidate: disburseValidateSlice.reducer,
  loanData: loandDataSlice.reducer,
})
